import React, { useEffect } from "react";
import PartSelect from "../PartSelect";
import GazeControl from "../GazeControl";
import ToHomeButton from "../buttons/ToHomeButton";
import ResumeNotice from "../helpers/exercise/ResumeNotice";
import ExerciseTabs from "../helpers/exercise/ExerciseTabs";
import ExerciseFinish from "../helpers/exercise/ExerciseFinish";
import { useExerciseProgress } from "../helpers/exercise/useExerciseProgress";
import { useExerciseStepLog } from "../helpers/exercise/useExerciseStepLog";
import ExerciseHeading from "../helpers/exercise/ExerciseHeading";
import { preloadImages } from '../../utils/imageCache';
import { getExerciseImageUrls } from '../../config/drawings';

// 演習で扱うのは2問だけ（2026-10-14週例。選び方は research/20261019_演習で扱う問題.docx）。
// 視点変更で説明できる4問のうち model4・cube_model3 はテストの転移課題に残す
// （cube_model3 は、演習で扱わない左右の視点移動＝視点を右にずらす考え方ができるかを見る問題）。
// 2026-10-19に問2を cube_model3 → cube_model5 に差し替えた：「おかしさが分かる視点」は画面が
// 目印と一緒に動く表示で、誤りが部品の回転でできている model1・cube_model5 では右の列でも
// 皿・上面が水平に戻っておかしさが消えて見えるが、cube_model3 は誤りが変形なので右の列では
// 形が変わらず、おかしさが解消されることが見えないため。2問とも上下の視点移動になる。
// 一致視点（matchGazePositions）は4問ぶん残してある。
export const GAZE_MODEL_MAP = [
    'model1',      // 問1: 皿を丸く描きすぎる誤り（視点を下げると一致）
    'cube_model5', // 問2: 立方体: 上面を縦に広く描く誤り（視点を下げると一致）
];

export default function Ex_1_2026_Gaze() {
    // 視点変更で「おかしさ」が解消できる誤りのみを扱う（候補は model1・model4・cube_model5・cube_model3。
    // 演習で使うのはそのうち2問＝GAZE_MODEL_MAP）。
    // 発散遠近（cube_model1/2）はどの実視点でも生じないため視点変更では扱わない
    // （形状変更専用。Ex1_2026_Shape側に残す）。
    const modelMap = GAZE_MODEL_MAP;
    const QUESTIONS = modelMap.length;
    const STEPS_PER_QUESTION = 2; // 各問あたりのステップ数: 0=選択, 1=視点操作
    const TOTAL_STEPS = QUESTIONS * STEPS_PER_QUESTION;
    const getGlbForQuestion = (qIdx) => modelMap[qIdx] || 'model1';

    // 進行状態は番号ごとに保存され、リロード・中断後も続きから再開できる
    const {
        index, questionIndex, isFinished, selectedParts,
        isResumedStep, isStepCompleted, getStepAnswer, canGoToIndex, goToIndex,
        setSelectedPart, markStepAnswered, completeStep,
    } = useExerciseProgress({
        exerciseKey: 'gaze',
        questions: QUESTIONS,
        stepsPerQuestion: STEPS_PER_QUESTION,
    });

    // 問×ステップを開くたびに、ログの文脈・所要時間の計測・表示の記録を切り替える
    useExerciseStepLog({ exercise: 'gaze', index, stepsPerQuestion: STEPS_PER_QUESTION, modelMap, active: !isFinished });
    // 全問の画像を先に読み込んでおく（途中で通信が切れても次の問の画像が出るように）
    useEffect(() => { preloadImages(getExerciseImageUrls(modelMap)); }, [modelMap]);

    const renderContentForIndex = (idx) => {
        const qIdx = Math.floor(idx / STEPS_PER_QUESTION);
        const stepWithin = idx % STEPS_PER_QUESTION;

        if (stepWithin === 0) {
            // 0: 選択系コンポーネント
            return (
                <PartSelect
                    key={`part-select-${qIdx}`}
                    number={qIdx + 1}
                    glbPath={getGlbForQuestion(qIdx)}
                    setSelectedPart={(val) => setSelectedPart(qIdx, val)}
                    // 回答済みの問へ戻ったときは当時の着色を復元し、選び直しと【次に進む】を止める
                    initialSelectedPart={selectedParts[qIdx]}
                    locked={isStepCompleted(idx)}
                    onComplete={(selected, camera, selfText, detail) => completeStep(selected, detail)}
                />
            );
        }
        // 1: 視点操作コンポーネント
        return (
            <div>
                <GazeControl
                    key={`gaze-control-${qIdx}`}
                    number={qIdx + 1}
                    selectedPart={selectedParts[qIdx]}
                    errorGlbPath={getGlbForQuestion(qIdx)}
                    isLastQuestion={idx >= TOTAL_STEPS - 1}
                    // 回答済みの問へ戻ったときは当時の回答を復元し、【決定】を止める
                    answer={getStepAnswer(idx)}
                    locked={isStepCompleted(idx)}
                    // 【決定】の時点で回答済みにする（「次の問題に進みます」の間にリロードしても答え直しにしない）
                    onAnswered={(detail) => markStepAnswered(selectedParts[qIdx], detail)}
                    onComplete={(selected, selfText, detail) => completeStep(selected, detail)}
                />
            </div>
        );
    };

    if (isFinished) {
        return <ExerciseFinish exerciseName="視点変更演習" />;
    }

    return (
        <div className="page">
            {/* 学習者向け見出し（内部のバージョン名・英語名は出さない） */}
            <ExerciseHeading title="視点変更演習" />

            <ResumeNotice questionNumber={isResumedStep ? questionIndex + 1 : null} />

            <ExerciseTabs
                totalSteps={TOTAL_STEPS}
                stepsPerQuestion={STEPS_PER_QUESTION}
                index={index}
                canGoToIndex={canGoToIndex}
                isStepCompleted={isStepCompleted}
                onSelect={goToIndex}
            />

            <div className="content">{renderContentForIndex(index)}</div>

            {/* 「前のステップに戻る」は置かない：回答済みのステップへ戻ると
                着色や自己説明の選択が作り直しになって消えるため（タブも同様に塞いでいる）。
                Home へ戻るボタンは X-2 のときだけ出す（X-1 は PartSelect が自分で出すので二重になる。
                GazeControl は /demo3 と共用なので中には置かない） */}
            {index % STEPS_PER_QUESTION === 1 && (
                <div className="row-center">
                    <ToHomeButton buttonText="Home画面に戻る" />
                </div>
            )}
        </div>
    );
}
